import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import styled from "@emotion/styled"
import { useLocation } from "react-router-dom"

import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useNavigation } from "contexts/Navigation"
import { useHeader, cssVarCurBanHeight } from "contexts/Header"

import { buttonReset } from "css/buttons"
import { legend100, normal } from "css/text"
import getMediaQuery from "css/breakpoints"
import { useUser } from "contexts/User"

import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { fullGrid } from "css/grid"

const Root = styled.div`
  grid-column: col 1 / span 5;
  grid-row: 3;
  align-self: start;
  margin-block-start: 5px;

  ${getMediaQuery("m")} {
    margin-block-start: unset;
    grid-column: col 6 / span 5;
    grid-row: 1;
    align-self: center;
  }

  &::after {
    content: "";
    display: block;
    width: 100%;
    border-bottom: solid 1px rgb(100, 100, 100);
  }
`

const Button = styled.button`
  ${buttonReset};
  width: 100%;
  justify-self: start;
  text-align: start;
  padding-block: 0 4px;
  display: flex;
  align-items: center;
  /* font-size: 14px; */
  ${legend100}
  ${getMediaQuery("m")} {
    /* font-size: 16px; */
    /* padding-block: 8px; */
  }

  span {
    margin-inline-start: auto;
    ::after {
      background: currentColor;
      content: "";
      display: block;
      height: 1em;
      mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15"><path d="M7.5,11L0.9,4H7h7L7.5,11z"/></svg>');
      pointer-events: none;
      width: 1em;
      transition: background-color 0.3s ease-out;
    }
  }
`

const PaneCont = styled(motion.div)`
  position: absolute;
  background: #212121;
  z-index: 10;
  padding-block: 20px;
  margin-top: 10px;
  inset-inline-start: 0;
  width: 100%;
  height: calc(100vh - var(${cssVarCurBanHeight}));
  ${fullGrid}

  ${getMediaQuery("m")} {
    display: block;
    width: auto;
    height: auto;
    width: calc(var(--grid-col-unit) * 5 + var(--grid-gap) * 4);
    padding-inline-start: calc(var(--grid-col-unit));
    /* padding-inline-end: 10px; */
    border-radius: 8px;
    inset-inline-start: unset;
    inset-inline-end: 22%;
    filter: drop-shadow(0px 2px 30px rgba(0 0 0 / 0.4));
  }
`

const Pane = styled(motion.ul)`
  grid-column: main;
  column-count: 2;
  ${getMediaQuery("m")} {
    padding-block-end: 20px;
  }
  /* padding-inline: calc(var(--outer-margin)); */
`

const Item = styled.li`
  padding-block: 6px;
  padding-inline-end: 20px;
`

const Link = styled.a`
  /* padding-inline: 40px; */

  position: relative;
  ${legend100};
  ${normal};

  font-size: 16px;

  text-decoration: none;
  transition: color 0.3s;
  will-change: color;
  color: rgb(var(--pure-white));

  /* white-space: nowrap; */

  &.active {
    color: rgb(var(--light-green));

    &::after {
      content: "";
      position: relative;
      display: inline-block;
      background-color: currentColor;
      border-radius: 50%;
      width: 0.5rem;
      height: 0.5rem;
      margin-inline-start: 0.5rem;
      margin-block-end: 0.1rem;
    }
  }

  @media (hover: hover) {
    &:hover {
      color: rgb(var(--light-green));
    }
  }
`

const LangPane = forwardRef(({ onClose, statut, setStatut }, btnRef) => {
  const console = useConsole()
  const env = useEnv()
  const navigation = useNavigation()
  const location = useLocation()
  const user = useUser()

  const {
    locales,
    current: {
      codes: { www: currLocale },
    },
  } = useLocale()

  const ref = useRef()
  const currentFocusRef = useRef()

  function setLocaleAndNavigate(e) {
    waTriggerEvent({
      eventName: "BannerLanguageClicked",
      eventType: "Click",
      bannerLanguage: {
        language: e.target.getAttribute("lang"),
      },
    })

    e.preventDefault()
    user.locale = e.target.getAttribute("lang")
    requestAnimationFrame(() => navigation.navigate(e.target.href, { preventLastLocation: true }, { replace: true }))
    onClose()
  }

  const variants = {
    closed: { y: 20, opacity: 0, transition: { duration: 0.35, ease: "linear" }, transitionEnd: { visibility: "hidden" } },
    opened: { y: 0, opacity: 1, visibility: "visible", transition: { duration: 0.35, ease: "linear" } },
  }

  function handleNextPrev(n) {
    if (n === "ArrowUp") {
      const node = currentFocusRef.current.parentNode.previousSibling
      node?.firstChild?.focus()
      currentFocusRef.current = document.activeElement
    } else if (n === "ArrowDown") {
      const node = currentFocusRef.current.parentNode.nextSibling
      node?.firstChild?.focus()
      currentFocusRef.current = document.activeElement
    }
  }

  function onKeyDownCapture(e) {
    if (e.key === "Escape") {
      setStatut("closed")
      btnRef.current.focus()
      return
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault()
      handleNextPrev(e.key)
    }
  }

  useEffect(() => {
    const cur = ref.current.querySelector(`[lang="${currLocale}"`)
    cur.focus()
    currentFocusRef.current = cur
  }, [])

  return (
    <PaneCont id='languagesPanel' variants={variants} initial='closed' animate={statut} onKeyDownCapture={onKeyDownCapture}>
      <Pane ref={ref}>
        {locales.map(locale => {
          let href =
            env.cn && locale.external
              ? new URL(navigation.localize(navigation.slug, locale.codes.www), "https://rolex.com").href
              : navigation.localize(navigation.slug, locale.codes.www)
          href += location.search

          return (
            <Item key={locale.codes.www}>
              <Link
                href={href}
                lang={locale.codes.www}
                dir={locale.dir}
                onClick={setLocaleAndNavigate}
                className={locale.codes.www === currLocale ? "active" : null}
                aria-current={locale.codes.www === currLocale || null}
                target='_self'
              >
                {locale.label}
              </Link>
            </Item>
          )
        })}
      </Pane>
    </PaneCont>
  )
})

export default function LangDropDown({ onClose }) {
  const console = useConsole()
  const {
    locales,
    current: {
      codes: { www: currLocale },
    },
  } = useLocale()

  const header = useHeader()

  const ref = useRef()
  const btnRef = useRef()

  const [statut, setStatut] = useState("closed")

  const lab = locales.find(l => l.codes.www === currLocale).label

  function onClick() {
    if (statut === "closed") {
      setStatut("opened")
    } else {
      setStatut("closed")
    }
  }

  function onHeaderUpdate(e) {
    if (e.t === 0) {
      if (statut === "opened") {
        setStatut("closed")
      }
    }
  }
  useLayoutEffect(() => header.on(header.events.update, onHeaderUpdate))

  function onBlur(e) {
    if (!ref.current.contains(e.relatedTarget)) {
      setStatut("closed")
    }
  }

  const toggleVariants = {
    closed: { rotate: 0, transition: { duration: 0.35, ease: "linear" } },
    opened: { rotate: 180, transition: { duration: 0.35, ease: "linear" } },
  }

  return (
    <Root ref={ref} onBlur={onBlur}>
      <Button
        ref={btnRef}
        type='button'
        aria-describedby='languageBannerText'
        aria-controls='languagesPanel'
        aria-expanded={statut === "opened"}
        onClick={onClick}
      >
        {lab}
        <motion.span variants={toggleVariants} initial='closed' animate={statut} />
      </Button>
      <LangPane ref={btnRef} onClose={onClose} statut={statut} setStatut={setStatut} />
      {/* <AnimatePresence>{statut === "opened" ? <LangPane ref={btnRef} onClose={onClose} setStatut={setStatut} /> : null}</AnimatePresence> */}
    </Root>
  )
}
